import React from 'react'
import { connect } from 'react-redux'
import { IGlobalState } from '../../Root/store/appReducer'
import {
  CustomType,
  GoalWithProgress,
  ProgressType,
  UserCustomDailyProgress,
  UserDailyDiaryProgress,
  UserDailyProgress,
} from '../../services/api.types'
import {
  getAllDailyProgressSelector,
  getCustomProgressRawSelector,
  getCustomProgressSelector,
  getDailyProgressSelector,
  getProgressResultsSelector,
} from '../../views/Progress/store/progressSelectors'
import { getCustomTypeWithLegacy, getUnit } from '../../services/CustomTypes'
import YumCustomTable from '../YumCustomTable/YumCustomTable'
import YumLogTable from '../YumCustomTable/YumLogTable'
import YumTotalPointsTableWrapper from '../YumCustomTable/YumTotalPointsTableWrapper'
import YumChallengeActivityIndexProgress from '../YumChallengeGoal/components/YumChallengeActivityIndexProgress/YumChallengeActivityIndexProgress'
import {
  getDailyValueGoal,
  getGoalConfigByType,
  getPersistenceDailyTotal,
  isActivityIndexType,
  isDistanceType,
  isPersistenceType,
  parseGoalsConfig,
} from '../../common/helpers/challenges'
import { AllCustomProgress, AllDailyDiary } from '../YumCustomEntry/YumCustomEntry'
import { t } from 'ttag'
import { isSimpleDayType } from '../../common/helpers/challenges'
import { CustomTypeCategory } from '../../services/CustomTypes'

/**
 * Props passed by parent component
 */
export interface YumLogDetailsTableWrapperOwnProps {
  goal: GoalWithProgress
}

/**
 * Props from Redux state
 */
interface StateProps {
  dailyDiary: UserDailyDiaryProgress[]
  allDailyDiary: AllDailyDiary
  customProgress: UserCustomDailyProgress[]
  allCustomProgress: AllCustomProgress
  progressResults: UserDailyProgress[]
}

type Props = YumLogDetailsTableWrapperOwnProps & StateProps

/**
 * Component that displays the details table for a goal.
 * Renders YumCustomTable, YumLogTable, or YumTotalPointsTableWrapper based on goal type.
 * Fetches required data from Redux state automatically.
 */
function YumLogDetailsTable(props: Props) {
  const { goal, dailyDiary, allDailyDiary, customProgress, allCustomProgress, progressResults } = props

  const {
    progressType,
    customType,
    customUnit,
    dailyPersistenceGoal,
    manualEntryEnabled,
    roundDailyPoints,
    oldRoundDailyPoints,
    dailyMax,
  } = goal

  // Determine goal type flags
  const isCustomCustom = customType === CustomType.CUSTOM
  const isPoints = customType === CustomType.POINTS
  const isTotalPoints = customType === CustomType.TOTAL_POINTS
  const isAnyActivityIndex = [ProgressType.ALL, ProgressType.PERSISTENCE_ALL].includes(progressType)
  const isPersistence = isPersistenceType(progressType)
  const isDistance = isDistanceType(customType)
  const isSimpleType = isSimpleDayType(customType as unknown as CustomTypeCategory)
  const simpleManualEntry = goal.simpleManualEntry && progressType === ProgressType.PERSISTENCE_CUSTOM

  // Unit label for the table
  const unitsLabel = getUnit(progressType, customType, customUnit) as string

  // Show legacy progress view for activity index types
  const showLegacyProgress = isAnyActivityIndex

  // Calculate value goal for custom table
  const customTableValueGoal = getDailyValueGoal(goal, progressType)

  // Parse points logic if applicable
  const pointsLogic = isPoints ? JSON.parse(goal.pointsLogic) : null

  // Daily max value (only for non-persistence and non-activity-index)
  const dailyMaxValue = dailyMax && dailyMax > 0 && !isPersistence && !isAnyActivityIndex ? dailyMax : null

  // Reverse data for display (most recent first)
  const tableDataReversed = customProgress ? customProgress.slice().reverse() : customProgress

  // Process daily diary data
  let dailyDiaryReversed = dailyDiary ? dailyDiary.slice().reverse() : dailyDiary

  // For activity index, convert progress results to daily diary format
  let activityIndexDayDetails: { [date: string]: UserDailyProgress } | undefined
  if (showLegacyProgress) {
    dailyDiaryReversed = progressResults
      .slice()
      .reverse()
      .map((userDailyProgress: UserDailyProgress) => {
        const manualValue = getPersistenceDailyTotal(userDailyProgress, progressType, true)
        const integrationValue = isAnyActivityIndex ? userDailyProgress.integrationTotalPercentage : manualValue
        return {
          date: userDailyProgress.date,
          integrationValue,
          manualValue: manualValue - integrationValue,
          sum: manualValue,
        } as UserDailyDiaryProgress
      })

    if (isAnyActivityIndex) {
      activityIndexDayDetails = progressResults.reduce(
        (acc, p) => {
          acc[p.date] = p
          return acc
        },
        {} as { [date: string]: UserDailyProgress },
      )
    }
  }

  // Render appropriate table based on goal type
  if (isTotalPoints) {
    return <YumTotalPointsTableWrapper goalId={goal.id} />
  }

  if ((isCustomCustom || isPoints) && !showLegacyProgress) {
    return (
      <YumCustomTable
        data={tableDataReversed}
        valueGoal={customTableValueGoal}
        unitsLabel={unitsLabel}
        units={isAnyActivityIndex ? '%' : ''}
        binary={progressType === ProgressType.PERSISTENCE_CUSTOM_BINARY || simpleManualEntry}
        showDigits={isDistance}
        pointsLogic={pointsLogic}
        allDailyDiary={allDailyDiary}
        allCustomProgress={allCustomProgress}
        manualEntryEnabled={manualEntryEnabled}
        roundDailyPoints={roundDailyPoints}
        oldRoundDailyPoints={oldRoundDailyPoints}
        dailyMax={dailyMaxValue}
      />
    )
  }

  // Goal value
  const valueGoal = isPersistence ? dailyPersistenceGoal || 0 : 1

  // if (isPersistence) {
  //   const goalsConfig = parseGoalsConfig(goal.goals)
  //   const goalConfig = getGoalConfigByType(goalsConfig, 'individual')
  //   if (isAnyActivityIndex) {
  //     valueGoal = goalConfig?.indexTarget || 100
  //   }
  //   // } else {
  //   //   valueGoal = goalConfig?.dailyGoal || valueGoal
  //   // }
  // }

  return (
    <>
      {isAnyActivityIndex && !manualEntryEnabled && <YumChallengeActivityIndexProgress goal={goal} />}
      <YumLogTable
        data={dailyDiaryReversed}
        valueGoal={valueGoal}
        units={isAnyActivityIndex ? t`Activity index` : unitsLabel}
        showDigits={isDistance}
        manualEntryEnabled={manualEntryEnabled}
        suffix={isAnyActivityIndex ? '%' : ''}
        isBinary={isSimpleType || simpleManualEntry}
        dailyMax={dailyMaxValue}
        activityIndexDayDetails={activityIndexDayDetails}
      />
    </>
  )
}

/**
 * Maps Redux state to component props.
 * Fetches daily diary, custom progress, and progress results for the goal.
 */
const mapStateToProps = (state: IGlobalState, ownProps: YumLogDetailsTableWrapperOwnProps): StateProps => {
  const { goal } = ownProps
  const goalId = goal.id

  return {
    dailyDiary: getDailyProgressSelector(state, getCustomTypeWithLegacy(goal.customType, goal.progressType)),
    allDailyDiary: getAllDailyProgressSelector(state),
    customProgress: getCustomProgressSelector(state, goalId),
    allCustomProgress: getCustomProgressRawSelector(state, goalId),
    progressResults: getProgressResultsSelector(state),
  }
}

/**
 * Wrapper component that connects YumLogDetailsTable to Redux.
 * Provides automatic data fetching for the details table.
 *
 * Usage:
 * <YumLogDetailsTableWrapper goal={goal} />
 */
export default connect(mapStateToProps)(YumLogDetailsTable)
